import React from 'react';
import { Badge, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { FC, useCallback } from 'react';
import { FaRegCommentDots } from 'react-icons/fa';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';
import { Nullable, TicketProvider } from 'common-types';
import CustomLink from '../../../../../elements/Aggregation/elements/Link';
import useJiraStore, {
  getJiraTicketsStatusUpdates,
} from '../../../../../jira/api/jira-api';
import {
  setDrawerJiraTicketsStatus,
  setJiraTicketsToRawIssue,
} from '../../../../active-issues/store-actions/issue-store-actions';
import IssuesStore from '../../../../active-issues/stores/issues-store';
import {
  FixIssue,
  FixType,
  JiraTicketRef,
  SlackNotification,
} from '../../../types/issues-types';
import { resolveActionIcon } from '../../../utils/issue-utils';
import { formatDistanceToNow } from 'date-fns';

let lastUpdatedJiraTicketsRowId: Nullable<string> = null;

const IssuesActions: FC<React.PropsWithChildren<IssuesActionsProps>> = ({
  jiraTickets,
  comment,
  fixIssue,
  issueId,
  slackNotification,
}) => {
  const { classes } = useStyles();
  const { isActiveJiraConnection, selectedIssueId } = useSnapshot(IssuesStore);
  const JiraIcon = resolveActionIcon(TicketProvider.Jira);
  const SlackIcon = resolveActionIcon(TicketProvider.Slack);
  const FixIcon = resolveActionIcon(FixType.PullRequest);
  const { loadingJiraTicketsStatusUpdates } = useJiraStore();

  const onMouseOut = useCallback(async () => {
    lastUpdatedJiraTicketsRowId = null;
  }, []);

  const onMouseEnter = useCallback(async () => {
    const jiraTicketStatusRes =
      isActiveJiraConnection &&
      lastUpdatedJiraTicketsRowId !== issueId &&
      (await getJiraTicketsStatusUpdates(
        jiraTickets.map(t => t.key),
        issueId,
        false,
      ));

    if (jiraTicketStatusRes) {
      const jiraTicketStatus = jiraTicketStatusRes.map(
        ({ key, self, id, fields }) => ({
          link: self,
          ticketId: id,
          key,
          fields,
        }),
      );
      setJiraTicketsToRawIssue(issueId, jiraTicketStatus);
      selectedIssueId === issueId &&
        setDrawerJiraTicketsStatus(jiraTicketStatus);
    }

    lastUpdatedJiraTicketsRowId = issueId;
  }, [isActiveJiraConnection, issueId, jiraTickets, selectedIssueId]);

  return (
    <div className={classes.container}>
      {isActiveJiraConnection && jiraTickets.length >= 1 && JiraIcon && (
        <Tooltip
          arrow
          placement='top'
          title={
            <div className={classes.actionsJira}>
              {!loadingJiraTicketsStatusUpdates
                ? jiraTickets.map((jiraTicket, index) => {
                    return (
                      <CustomLink
                        key={index}
                        href={jiraTicket.link}
                        value={`Jira ticket #${jiraTicket.key} [${jiraTicket?.fields?.status.name}]`}
                      />
                    );
                  })
                : 'Checking status...'}
            </div>
          }>
          {jiraTickets.length === 1 ? (
            <IconButton
              onMouseOut={onMouseOut}
              onMouseEnter={onMouseEnter}
              href={jiraTickets[0].link}
              target='_blank'
              style={{ backgroundColor: 'transparent' }}>
              <JiraIcon />
            </IconButton>
          ) : (
            <IconButton
              onMouseOut={onMouseOut}
              onMouseEnter={onMouseEnter}
              style={{ backgroundColor: 'transparent' }}>
              <JiraIcon />
            </IconButton>
          )}
        </Tooltip>
      )}
      {comment && (
        <Tooltip
          arrow
          placement='top'
          title={
            <Typography variant='body2' color='text.primary'>
              {comment}
            </Typography>
          }>
          <IconButton
            color='primary'
            size='small'
            style={{ backgroundColor: 'transparent' }}>
            <FaRegCommentDots size='18px' />
          </IconButton>
        </Tooltip>
      )}
      {fixIssue && fixIssue.activeFix && FixIcon && (
        <Tooltip
          arrow
          placement='top'
          title={
            <Link href={fixIssue.activeFix.fixURL} target='_blank'>
              <Typography variant='body2' color='text.primary'>
                {fixIssue.activeFix.fixURL}
              </Typography>
            </Link>
          }>
          <IconButton
            href={fixIssue.activeFix.fixURL}
            target='_blank'
            style={{ backgroundColor: 'transparent' }}>
            <FixIcon />
          </IconButton>
        </Tooltip>
      )}
      {slackNotification && slackNotification.length > 0 && SlackIcon && (
        <Tooltip
          arrow
          placement='top'
          title={
            <React.Fragment>
              {slackNotification.map(({ channel, timestamp }) => {
                return (
                  <Typography
                    noWrap
                    key={timestamp}
                    variant='body2'
                    color='text.primary'>
                    {`Message sent to "${channel}" (${formatDistanceToNow(
                      new Date(Number(timestamp)),
                      {
                        addSuffix: true,
                      },
                    )})`}
                  </Typography>
                );
              })}
            </React.Fragment>
          }>
          <Badge
            sx={{
              '& .MuiBadge-badge': {
                minWidth: 0,
                width: 12,
                height: 12,
                fontSize: 10,
              },
            }}
            variant='standard'
            color='error'
            badgeContent={slackNotification.length}>
            <SlackIcon />
          </Badge>
        </Tooltip>
      )}
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionsJira: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export interface IssuesActionsProps {
  jiraTickets: JiraTicketRef[];
  comment: string;
  fixIssue: Nullable<FixIssue>;
  issueId: string;
  slackNotification?: SlackNotification[];
}

export default IssuesActions;
